import { Link, useParams } from "react-router-dom";
import { HomeOutlined } from '@ant-design/icons';
import { useCompany } from "../../../hooks/useCompany";
import { Button, DatePicker, Form, Input, Select, InputNumber } from "antd";
import "./styles.css";
import { useForm } from "react-hook-form";
import useLoading from "../../../hooks/useLoading";
import { api } from "../../../services/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { macroStepsModel } from "../../../models/macroStepsModel";
import dayjs, { Dayjs } from "dayjs";


interface CreateMaterialForm {
    CNPJ: string;
    AssociatedCompany: string;
    Identity: string;
    Supplier: string;
    Description: string;
    Quantity: number;
    Purpose: string;
    CostCenter: string;
    AccountingAccountNumber: string;
    NDoc: string;
    TypeAppropriation: string;
    AccountingDate: string | null;
    ValueNF: number;
    AccountedValue: number;
    ProjectName: string;
    MacroStageProject: string;
}

interface OptionsProps {
    value: string;
    label: string;
    disabled: boolean;
}
interface AssociatedCompanyProps {
    cnpj: string;
    companyName: string;
}

export default function CreateMaterials() {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const [associatedCNPJ, setAssociatedCNPJ] = useState<OptionsProps[]>([]);
    const [identitys, setIdentitys] = useState<OptionsProps[]>([]);
    const [purposes, setPurposes] = useState<OptionsProps[]>([]);
    const [projectsNames, setProjectsNames] = useState<OptionsProps[]>([]);
    const [associatedCompanyInfo, setAssociatedCompanyInfo] = useState<AssociatedCompanyProps[]>([]);
    const { company } = useCompany();
    const { toggleLoading } = useLoading();
    const navigate = useNavigate();
    const { Option } = Select;
    const { TextArea } = Input;
    const [associatedCNPJSelected, setAssociatedCNPJSelected] = useState<string>()
    const [associatedCompanySelected, setAssociatedCompanySelected] = useState<string>()
    const [identitySelected, setIdentitySelected] = useState<string>()
    const [purposeSelected, setPurposeSelected] = useState<string>()
    const [projectIdSelected, setProjectIdSelected] = useState<string>()
    const [projectNameLabel, setProjectNameLabel] = useState<string>("")
    const [macroSteps, setMacroSteps] = useState<macroStepsModel[]>([]);
    const [macroStepsSelected, setMacroStepsSelected] = useState<string>();
    const [supplierValue, setSupplierValue] = useState<string>("");
    const [descriptionValue, setDescriptionValue] = useState<string>("");
    const [quantityValue, setQuantityValue] = useState<number | null>(null);
    const [costCenterValue, setCostCenterValue] = useState<string>("");
    const [accountingAccountNumberValue, setAccountingAccountNumberValue] = useState<string>("");
    const [nDocValue, setNDocValue] = useState<string>("");
    const [typeAppropriationValue, setTypeAppropriationValue] = useState<string>("");
    const [appropriationTypes, setAppropriationTypes] = useState<OptionsProps[]>([]);
    const [valueNFValue, setValueNFValue] = useState<number | null>(null);
    const [accountedValueValue, setAccountedValueValue] = useState<number | null>(null);
    const [accountingDateValue, setAccountingDateValue] = useState<Dayjs | null>(null);


    const { handleSubmit, setValue } = useForm<CreateMaterialForm>({
    });

    async function handleSaveMaterial(data: any) {
        toggleLoading(true);
        let material = data;
        material.serviceId = company.serviceId;
        if (isEditMode && id) {
            material.materialId = id;
        }
        material.projectName = projectNameLabel;

        try {
            const request = isEditMode ? api.put('Material/UpdateMaterial', material) : api.post('Material/CreatedMaterial', material);
            await request.then((resp) => {
                const response = resp.data;

                if (response.statusCode === 200) {
                    toast.success(`Material ${isEditMode ? 'atualizado' : 'cadastrado'} com sucesso!`);
                    navigate(`/${company.serviceId}/expenditure`);
                } else {
                    toast.error(response.message ?? 'Erro ao salvar material.');
                }
            });
        }
        catch (error: any) {
            toast.error(error);
        }
        finally {
            toggleLoading(false);
        }
    }

    async function loadMaterialDetails() {
        if (!id) return;
        toggleLoading(true);
        try {
            const resp = await api.get(`Material/GetById/${id}`);
            const response = resp.data;

            if (response.statusCode === 200 && response.data) {
                const material = response.data;

                setAssociatedCNPJSelected(material.associatedCNPJ);
                setAssociatedCompanySelected(material.associatedCompany);
                setValue("AssociatedCompany", material.associatedCompany);
                setValue("CNPJ", material.associatedCNPJ);

                const identityValue = material.identity?.toString() ?? "";
                setIdentitySelected(identityValue);
                setValue("Identity", identityValue);

                const purposeValue = material.purpose?.toString() ?? "";
                setPurposeSelected(purposeValue);
                setValue("Purpose", purposeValue);

                setSupplierValue(material.supplier ?? "");
                setValue("Supplier", material.supplier);

                setDescriptionValue(material.description ?? "");
                setValue("Description", material.description);

                setQuantityValue(material.quantity ?? null);
                setValue("Quantity", material.quantity);

                setCostCenterValue(material.costCenter ?? "");
                setValue("CostCenter", material.costCenter);

                setAccountingAccountNumberValue(material.accountingAccountNumber ?? "");
                setValue("AccountingAccountNumber", material.accountingAccountNumber);

                setNDocValue(material.nDoc ?? "");
                setValue("NDoc", material.nDoc);

                setTypeAppropriationValue(material.typeApropriation ?? "");
                setValue("TypeAppropriation", material.typeApropriation);

                const accountingDate = material.accountingDate ? dayjs(material.accountingDate) : null;
                setAccountingDateValue(accountingDate);
                setValue("AccountingDate", accountingDate ? accountingDate.format("DD/MM/YYYY") : null);

                setValueNFValue(material.valueNF ?? null);
                setValue("ValueNF", material.valueNF);

                setAccountedValueValue(material.accountedValue ?? null);
                setValue("AccountedValue", material.accountedValue);

                const projectOption = projectsNames.find(p => p.label === material.projectName);
                const projectId = projectOption?.value ?? "";
                setProjectIdSelected(projectId);
                setProjectNameLabel(material.projectName ?? "");

                if (projectId) {
                    await fetchMacroSteps(projectId);
                }

                setMacroStepsSelected(material.macroStageProject ?? "");
                setValue("MacroStageProject", material.macroStageProject ?? "");
            } else {
                toast.error("Não foi possível carregar o material.");
            }
        } catch (error: any) {
            toast.error("Não foi possível carregar os dados do material.");
        } finally {
            toggleLoading(false);
        }
    }

    async function goToExpenditure() {
        navigate(`/${company.serviceId}/expenditure`);
    }

    async function fetchMacroSteps(projectId: string) {
        setMacroSteps([]);
        setMacroStepsSelected("");
        setValue("MacroStageProject", "");

        if (projectId) {
            toggleLoading(true);
            try {
                const response = await api.get(`MacroSteps/GetAllByProjectId/${company.serviceId}/${projectId}`);
                const data = response.data ?? [];
                const validMacroSteps = Array.isArray(data) ? data.filter((item: any) => item && item.id) : [];
                setMacroSteps(validMacroSteps);
            } catch (error: any) {
                toast.error("Falha ao buscar as Macro Etapas do projeto.");
                console.error(error);
            } finally {
                toggleLoading(false);
            }
        }
    }


    async function getAllSelects() {
        toggleLoading(true);
        try {
            await api.get(`Material/GetAllSelects/${company.serviceId}/${company.companyId}`,).then((resp) => {
                const response = resp.data;

                if (response.statusCode === 200) {
                   
                    console.log(response);
                    setAssociatedCNPJ(response.data.associatedCNPJ);
                    setIdentitys(response.data.identitys);
                    setPurposes(response.data.purpose);
                    setProjectsNames(response.data.projectsNames);
                    setAssociatedCompanyInfo(response.data.associatedCompanysInfo);
                    if (response.data.appropriationTypes) {
                        setAppropriationTypes(
                            response.data.appropriationTypes.map((o: any) => ({ value: o.value, label: o.label, disabled: false }))
                        );
                    }


                }
            });
        }
        catch (error: any) {
            toast.error(error);
        }
        finally {
            toggleLoading(false);
        }
    }
    useEffect(() => {
        getAllSelects()
    }, [])

    useEffect(() => {
        if (isEditMode && projectsNames.length > 0) {
            loadMaterialDetails();
        }
    }, [isEditMode, projectsNames.length]);



    return (
        <>
            <div className='card '>
                <div className='breadCrumb header-card-path ' >
                    <Link to={`/${company.serviceId}/home`} className='d-flex align-center header-path'>
                        <HomeOutlined className='breadCrumb-item-actual header-path-icon-home' />
                    </Link>
                    <span className='breadCrumb-separator header-path'>
                        <Link to={`/${company.serviceId}/home`} className='header-path '>
                            Homepage
                        </Link>
                    </span>

                    <span className='header-path breadCrumb-separator'>{' / '}
                    </span>
                    <span className='header-path breadCrumb-separator'>
                        <Link to={`/${company.serviceId}/expenditure`} className='header-path '>
                            Dispêndios
                        </Link>

                    </span>
                    <span className='header-path breadCrumb-separator'>{' / '}
                    </span>

                    <span className='header-path-actual breadCrumb-separator'>
                        {isEditMode ? "Edição" : "Criação"} de Dispêndio - Materias de Consumo
                    </span>
                </div>

                <Form onFinish={handleSubmit(handleSaveMaterial)} layout="vertical">
                    <div className="mx-4">
                        <p className="main-title">{isEditMode ? "Edição" : "Cadastro"} - Materiais de Consumo</p>
                    </div>


                    <div className="d-flex justify-content-between align-items-center mx-4 mb-2">
                        <div className="w-25 pe-4 div-inputs">
                            <label className='form-label content-color'>CNPJ Associado: </label>
                            <Select
                                className="w-100 me-4 mb-3"
                                placeholder='Selecione uma opção'
                                dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                                optionLabelProp="label"
                                style={{ height: "40px" }}
                                value={associatedCNPJSelected}
                                onChange={(value) => {
                                    const companyAssociated = associatedCompanyInfo.find(item => item.cnpj === value);
                                    setAssociatedCompanySelected(companyAssociated ? companyAssociated.companyName : "");
                                    setValue("AssociatedCompany", companyAssociated ? companyAssociated.companyName : "")
                                    setAssociatedCNPJSelected(value);
                                    setValue("CNPJ", value);
                                }}
                            >
                                {
                                    associatedCNPJ != undefined ?
                                        associatedCNPJ.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())).map((item) => (
                                            <Option key={item.value} value={item.label}>
                                                {item.label}
                                            </Option>
                                        ))
                                        :
                                        <></>
                                }


                            </Select>
                        </div>

                        <div className="w-25 pe-4">
                            <label className='form-label content-color'>Empresa Associada: </label>
                            <Input
                                size="large"
                                placeholder="empresa associada teste"
                                value={associatedCompanySelected}
                                disabled={true}
                            />
                        </div>

                        <div className="w-25 pe-4 div-inputs">
                            <label className='form-label content-color'>Idêntidade: </label>

                            <Select
                                className="w-100 me-4 mb-3"
                            placeholder='Selecione uma opção'
                            dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                            optionLabelProp="label"
                            style={{ height: "40px" }}
                            value={identitySelected}
                            onChange={(value) => {
                                    setIdentitySelected(value);
                                    setValue("Identity", value);
                            }}
                        >
                                {
                                    identitys != undefined ?
                                        identitys.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())).map((item) => (
                                            <Option key={item.value} value={item.value} label={item.label}>
                                                {item.label}
                                            </Option>
                                        ))
                                        :
                                        <></>
                                }


                            </Select>
                        </div>

                        <div className="w-25">
                            <label className='form-label content-color'>Fornecedor</label>
                            <Input
                                size="large"
                                placeholder=""
                                value={supplierValue}
                                onChange={(e) => {
                                    setSupplierValue(e.target.value);
                                    setValue("Supplier", e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="mx-4 mb-2">
                        <label className='form-label content-color'>Descrição: </label>
                        <TextArea
                            rows={4}
                            placeholder="Digite Aqui..."
                            className="w-100"
                            value={descriptionValue}
                            onChange={(e) => {
                                setDescriptionValue(e.target.value);
                                setValue("Description", e.target.value);
                            }}
                        />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mx-4 mb-2 ">
                        <div className="w-25 pe-4 div-inputs d-flex flex-column">
                            <label className='form-label content-color'>Quantidade : </label>
                            <InputNumber
                                formatter={value => {
                                    return value?.toString() ? value.toString().replace('.', ',') : '';
                                }}
                                parser={value => {
                                    return Number(value?.toString().replace(',', '.'));
                                }}
                                size="large"
                                style={{ width: '100%' }}
                                placeholder="00,00"
                                value={quantityValue ?? undefined}
                                onChange={(value) => {
                                    const numericValue = value !== null && value !== undefined ? Number(value) : null;
                                    setQuantityValue(numericValue);
                                    setValue("Quantity", numericValue ?? 0);
                                }}
                            />
                        </div>

                        <div className="w-25 pe-4 div-inputs">
                            <label className='form-label content-color'>Finalidade : </label>
                            <Select
                                className="w-100 me-4 mb-3"
                            placeholder='Selecione uma opção'
                            dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                            optionLabelProp="label"
                            style={{ height: "40px" }}
                            value={purposeSelected}
                            onChange={(value) => {
                                    setPurposeSelected(value);
                                    setValue("Purpose", value);
                            }}
                        >
                                {
                                    purposes != undefined ?
                                        purposes.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())).map((item) => (
                                            <Option key={item.value} value={item.value} label={item.label}>
                                                {item.label}
                                            </Option>
                                        ))
                                        :
                                        <></>
                                }


                            </Select>
                        </div>

                        <div className="w-25 pe-4">
                            <label className='form-label content-color'>Centro de custo: </label>
                            <Input
                                size="large"
                                placeholder="Centro de custo teste"
                                value={costCenterValue}
                                onChange={(e) => {
                                    setCostCenterValue(e.target.value);
                                    setValue("CostCenter", e.target.value);
                                }}
                            />
                        </div>

                        <div className="w-25">
                            <label className='form-label content-color'>Nº Conta Contábil</label>
                            <Input
                                size="large"
                                placeholder="Nº Conta Contábil Teste"
                                value={accountingAccountNumberValue}
                                onChange={(e) => {
                                    setAccountingAccountNumberValue(e.target.value);
                                    setValue("AccountingAccountNumber", e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="mx-4 mb-2">
                        <label className="form-label content-color">Nº Documento</label>
                        <Input
                            size="large"
                            placeholder="00000000000000"
                            className="w-100"
                            value={nDocValue}
                            onChange={(e) => {
                                setNDocValue(e.target.value);
                                setValue("NDoc", e.target.value);
                            }}
                        />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mx-4 mb-2">
                        <Form.Item label="Tipo de Apropriação" className="w-25 pe-4" required>
                            <Select
                                placeholder='Selecione um tipo'
                                size="large"
                                style={{ height: "40px", width: "100%" }}
                                value={typeAppropriationValue}
                                onChange={(v) => {
                                    setTypeAppropriationValue(v);
                                    setValue("TypeAppropriation", v);
                                }}
                            >
                                {appropriationTypes.map(item => (
                                    <Option key={item.value} value={item.value}>{item.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label="Data da Contabilização" className="w-25 pe-4" required>
                            <DatePicker
                                format={"DD/MM/YYYY"}
                                placeholder="Clique para selecionar uma data"
                                style={{ height: "40px", width: "100%" }}
                                value={accountingDateValue}
                                onChange={(date) => {
                                    if (date) {
                                        setAccountingDateValue(date);
                                        const jsDate = date.toDate();
                                        const day = String(jsDate.getDate()).padStart(2, '0');
                                        const month = String(jsDate.getMonth() + 1).padStart(2, '0');
                                        const year = jsDate.getFullYear();
                                        const formattedDate = `${day}/${month}/${year}`;
                                        setValue("AccountingDate", formattedDate);
                                    } else {
                                        setAccountingDateValue(null);
                                        setValue("AccountingDate", null);
                                    }
                                }}
                            />
                        </Form.Item>

                        <Form.Item label="Valor Nota Fiscal" className="w-25 pe-4" required>
                            <InputNumber
                                formatter={value => {
                                    return value?.toString() ? value.toString().replace('.', ',') : '';
                                }}
                                parser={value => {
                                    return Number(value?.toString().replace(',', '.'));
                                }}
                                style={{ width: '100%' }}
                                size="large"
                                placeholder="00,00"
                                value={valueNFValue ?? undefined}
                                onChange={(value) => {
                                    const numericValue = value !== null && value !== undefined ? Number(value) : null;
                                    setValueNFValue(numericValue);
                                    setValue("ValueNF", numericValue ?? 0);
                                }}
                            />
                        </Form.Item>

                        <Form.Item label="Valor Contabilizado" className="w-25 pe-4" required>
                            <InputNumber
                                formatter={(value) => {
                                    if (!value) return '';
                                    const parts = String(value).split('.');
                                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                    return `R$ ${parts.join(',')}`;
                                }}
                                parser={(value) => {
                                    if (!value) return 0;
                                    const numericString = value.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.');
                                    return Number.isNaN(parseFloat(numericString)) ? 0 : parseFloat(numericString);
                                }}
                                style={{ width: '100%' }}
                                size="large"
                                placeholder="00,00"
                                value={accountedValueValue ?? undefined}
                                onChange={(value) => {
                                    const numericValue = value !== null && value !== undefined ? Number(value) : null;
                                    setAccountedValueValue(numericValue);
                                    setValue("AccountedValue", numericValue ?? 0);
                                }}
                            />
                        </Form.Item>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mx-4 mb-2">
                        <div className="w-50 pe-4 div-inputs">
                            <label className='form-label content-color'>Nome do Projeto : </label>
                            <Select
                                    className="w-100 me-4 mb-3"
                                    placeholder='Selecione uma opção'
                                    dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                                    optionLabelProp="label"
                                    style={{ height: "40px" }}
                                    value={projectIdSelected}
                                    onChange={async (projectId) => {
                                        const selectedOption = projectsNames.find(item => item.value === projectId);
                                        const projectName = selectedOption ? selectedOption.label : "";
                                        setProjectIdSelected(projectId);
                                        setProjectNameLabel(projectName);
                                        setValue("ProjectName", projectName);

                                        await fetchMacroSteps(projectId);
                                    }}
                                >
                                {
                                    projectsNames != undefined ?
                                    projectsNames.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())).map((project) => (
                                        <Option key={project.value} value={project.value} label={project.label}>
                                            {project.label}
                                        </Option>
                                    ))
                                        :
                                        <></>
                                }
                            </Select>
                        </div>

                        <div className="w-50 pe-4 div-inputs">
                            <label className='form-label content-color'>Macro Etapa do Projeto : </label>
                            <Select
                                className="w-100 me-4 mb-3"
                                placeholder="Selecione uma opção"
                                dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                                optionLabelProp="label"
                                style={{ height: "40px" }}
                                value={macroStepsSelected}                            
                                onChange={(value) => {
                                        const selected = macroSteps.find(item => item.macroSteps === value);
                                        
                                        setMacroStepsSelected(selected ? selected.macroSteps : "");
                                        setValue("MacroStageProject", selected?.macroSteps ?? "");
                                }}
                            >
                                {macroSteps?.map(stage => (
                                <Option key={stage.id} value={stage.macroSteps} label={stage.macroSteps}>
                                    {stage.macroSteps}
                                </Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end align-items-center m-4">
                        <Button style={{ height: "40px", marginRight: "10px" }} onClick={goToExpenditure}>
                            <div>
                                Voltar
                            </div>
                        </Button>

                        <Button className='material-button-blue' type='primary' style={{ height: "40px" }} htmlType='submit' >
                            <div className='text-white'>
                                Salvar
                            </div>
                        </Button>
                    </div>
                </Form>
            </div >
        </>
    )
}
